import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Loader2, Users, Eye, CalendarCheck, TrendingUp, Clock, MapPin, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dayDetail, setDayDetail] = useState(null);
  const [dayLoading, setDayLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/admin/analytics", { params: { days: 30 } }),
      api.get("/admin/upcoming", { params: { days: 2 } }),
    ])
      .then(([a, u]) => {
        setData(a.data);
        setUpcoming(u.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const openDay = async (isoDate) => {
    setDayLoading(true);
    setDayDetail({ date: isoDate, views: 0, sources: {}, top_referrers: [] });
    try {
      const r = await api.get(`/admin/analytics/day?date=${isoDate}`);
      setDayDetail(r.data);
    } catch {
      toast.error("Couldn't load that day's detail.");
    } finally { setDayLoading(false); }
  };

  if (loading)
    return (
      <div className="py-16 flex items-center justify-center text-ink-muted">
        <Loader2 size={20} className="animate-spin mr-2" /> Loading analytics…
      </div>
    );

  if (!data) return <div className="py-12 text-ink-soft">No analytics data.</div>;

  const { series, totals, today } = data;
  const maxVisitors = Math.max(1, ...series.map((d) => d.visitors));
  const todaySources = data.today_sources || {};
  const todayTopReferrers = data.today_top_referrers || [];

  return (
    <div className="space-y-10" data-testid="admin-dashboard">
      {/* Top tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile label="Today's visitors" value={today.visitors} icon={Users} accent />
        <Tile label="Today's bookings" value={today.bookings} icon={CalendarCheck} />
        <Tile label="Today's wellbeing checks" value={today.wellbeing_completed || 0} icon={Sparkles} />
        <Tile label="30-day conversion" value={`${totals.conversion_rate}%`} icon={TrendingUp} />
      </div>

      {/* Today panel — live view */}
      <TodayPanel today={today} sources={todaySources} topReferrers={todayTopReferrers} />

      {/* Upcoming schedule (today + tomorrow) */}
      {upcoming && <UpcomingSchedule data={upcoming} />}

      {/* Visitors chart — click any day for a drill-in */}
      <div className="bg-cream-soft border border-sand/70 rounded-3xl p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-ink-soft mb-1">Visitors · last 30 days</div>
            <div className="font-serif text-2xl text-ink">{totals.visitors.toLocaleString()} visitors · {totals.page_views.toLocaleString()} page views</div>
          </div>
          <div className="text-[11px] text-ink-soft">Click any bar for that day's sources</div>
        </div>
        <div className="flex items-end gap-1 h-44">
          {series.map((d) => {
            const h = Math.max(2, Math.round((d.visitors / maxVisitors) * 100));
            return (
              <button
                key={d.date}
                type="button"
                onClick={() => openDay(d.date)}
                data-testid={`analytics-day-bar-${d.date}`}
                className="flex-1 group relative cursor-pointer h-full flex items-end"
                title={`${d.date}: ${d.visitors} visitors`}
              >
                <div
                  className="w-full rounded-t-md bg-navy/15 group-hover:bg-navy transition-colors duration-300"
                  style={{ height: `${h}%`, minHeight: "4px" }}
                />
                <div className="hidden group-hover:block absolute -top-8 left-1/2 -translate-x-1/2 bg-ink text-cream text-[10px] px-2 py-1 rounded whitespace-nowrap pointer-events-none">
                  {d.visitors}
                </div>
              </button>
            );
          })}
        </div>
        <div className="flex justify-between mt-3 text-[11px] text-ink-soft">
          <span>{series[0]?.date}</span>
          <span>{series[series.length - 1]?.date}</span>
        </div>
      </div>

      {/* Bookings + enquiries + wellbeing charts */}
      <div className="grid md:grid-cols-3 gap-4">
        <MiniChart title="Bookings" total={totals.bookings} series={series} field="bookings" color="#0E3B4A" />
        <MiniChart title="Enquiries" total={totals.enquiries} series={series} field="enquiries" color="#3C5A68" />
        <MiniChart title="Wellbeing checks" subtitle={`${totals.wellbeing_started || 0} started · ${totals.wellbeing_completed || 0} completed`} total={totals.wellbeing_completed || 0} series={series} field="wellbeing_completed" color="#7C9B7C" />
      </div>

      {/* Traffic sources */}
      <TrafficSources sources={data.sources || {}} topReferrers={data.top_referrers || []} />

      {/* Client-reported referrals (the "How did you hear about us?" answers) */}
      <BookingReferrals items={data.booking_referrals || []} />

      {/* Day drill-in modal */}
      <DayDetailModal open={!!dayDetail} loading={dayLoading} detail={dayDetail} onClose={() => setDayDetail(null)} />
    </div>
  );
}

function TodayPanel({ today, sources, topReferrers }) {
  const totalToday = (sources.direct || 0) + (sources.google || 0) + (sources.social || 0) + (sources.other || 0);
  const lanes = [
    { key: "direct", label: "Direct", color: "bg-navy" },
    { key: "google", label: "Google", color: "bg-sage-deep" },
    { key: "social", label: "Social", color: "bg-clay-deep" },
    { key: "other", label: "Other", color: "bg-navy-light" },
  ];
  return (
    <div className="rounded-3xl border border-sand/70 bg-cream-soft p-6 md:p-8" data-testid="today-panel">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-ink-soft mb-1">Today · live</div>
          <div className="font-serif text-2xl text-ink">
            {today.visitors === 0 && totalToday === 0 ? "No visits yet today." : `${today.visitors} visitor${today.visitors === 1 ? "" : "s"} · ${today.page_views} view${today.page_views === 1 ? "" : "s"}`}
          </div>
        </div>
        <div className="text-[11px] text-ink-soft">{new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" })}</div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-2">Where they came from</div>
          {totalToday === 0 ? (
            <div className="text-sm text-ink-soft italic">Nothing to show yet.</div>
          ) : (
            <div className="space-y-2">
              {lanes.map((l) => {
                const v = sources[l.key] || 0;
                const pct = totalToday ? Math.round((v / totalToday) * 100) : 0;
                return (
                  <div key={l.key} className="grid grid-cols-12 gap-3 items-center text-sm">
                    <div className="col-span-3 flex items-center gap-2 text-ink"><span className={`h-2 w-2 rounded-full ${l.color}`} /> {l.label}</div>
                    <div className="col-span-7 h-2 bg-sand/50 rounded-full overflow-hidden">
                      <div className={`h-full ${l.color} rounded-full`} style={{ width: `${pct}%` }} />
                    </div>
                    <div className="col-span-2 text-right tabular-nums text-ink-muted text-xs">{v} · {pct}%</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-2">Top referrers today</div>
          {topReferrers.length === 0 ? (
            <div className="text-sm text-ink-soft italic">No referrers logged yet.</div>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {topReferrers.slice(0, 6).map((r) => (
                <li key={r.host} className="flex items-center justify-between border-b border-sand/40 py-1">
                  <span className="text-ink truncate">{r.host}</span>
                  <span className="tabular-nums text-ink-soft">{r.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="mt-5 pt-4 border-t border-sand/60 flex flex-wrap gap-6 text-sm">
        <div><span className="text-ink-soft mr-2">Bookings today:</span><span className="font-medium text-ink tabular-nums">{today.bookings}</span></div>
        <div><span className="text-ink-soft mr-2">Enquiries today:</span><span className="font-medium text-ink tabular-nums">{today.enquiries}</span></div>
        <div><span className="text-ink-soft mr-2">Wellbeing checks:</span><span className="font-medium text-ink tabular-nums">{today.wellbeing_started || 0} started · {today.wellbeing_completed || 0} completed</span></div>
      </div>
    </div>
  );
}

function DayDetailModal({ open, loading, detail, onClose }) {
  if (!open || !detail) return null;
  const total = (detail.sources?.direct || 0) + (detail.sources?.google || 0) + (detail.sources?.social || 0) + (detail.sources?.other || 0);
  return (
    <div className="fixed inset-0 z-[70] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="day-detail-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-cream rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="px-6 py-5 flex items-start justify-between border-b border-sand/60">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-ink-soft mb-1">Traffic detail</div>
            <div className="font-serif text-2xl text-ink">{format(new Date(detail.date), "EEEE · d MMM yyyy")}</div>
          </div>
          <button type="button" onClick={onClose} data-testid="day-detail-close" className="h-8 w-8 rounded-full text-ink-soft hover:bg-sand/40 flex items-center justify-center"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-5">
          {loading ? (
            <div className="py-8 flex items-center justify-center text-ink-muted"><Loader2 size={16} className="animate-spin mr-2" /> Loading…</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3">
                <MiniStat label="Views" value={detail.views} />
                <MiniStat label="WB started" value={detail.wellbeing_started} />
                <MiniStat label="WB completed" value={detail.wellbeing_completed} />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-2">Sources</div>
                {total === 0 ? <div className="text-sm text-ink-soft italic">No views on this day.</div> : (
                  <div className="space-y-1.5 text-sm">
                    {Object.entries(detail.sources || {}).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between">
                        <span className="capitalize text-ink">{k}</span>
                        <span className="tabular-nums text-ink-soft">{v}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {detail.top_referrers?.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-2">Top referrers</div>
                  <ul className="space-y-1 text-sm">
                    {detail.top_referrers.map((r) => (
                      <li key={r.host} className="flex items-center justify-between border-b border-sand/40 py-1">
                        <span className="text-ink truncate">{r.host}</span>
                        <span className="tabular-nums text-ink-soft">{r.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-xl bg-cream-soft border border-sand/60 p-3 text-center">
      <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft">{label}</div>
      <div className="font-serif text-2xl text-ink tabular-nums">{value ?? 0}</div>
    </div>
  );
}

function BookingReferrals({ items }) {
  const total = items.reduce((s, i) => s + i.count, 0);
  return (
    <div className="bg-cream-soft border border-sand/70 rounded-3xl p-6 md:p-8" data-testid="booking-referrals">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-ink-soft mb-1">How clients heard about us · last 30 days</div>
          <div className="font-serif text-2xl text-ink">
            {total === 0 ? "No answers yet." : `${total} booking${total === 1 ? "" : "s"} answered`}
          </div>
        </div>
      </div>
      {total === 0 ? (
        <div className="text-sm text-ink-soft italic">
          Clients pick from a dropdown on the last booking step. Their answers will appear here.
        </div>
      ) : (
        <div className="bg-cream rounded-xl border border-sand/60 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-sand/20 text-ink-soft text-[11px] uppercase tracking-[0.16em]">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Source</th>
                <th className="text-right px-4 py-2 font-medium">Bookings</th>
                <th className="text-right px-4 py-2 font-medium">Share</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const pct = total ? ((r.count / total) * 100).toFixed(0) : 0;
                return (
                  <tr key={r.source} className="border-t border-sand/40">
                    <td className="px-4 py-2.5 text-ink">{r.source}</td>
                    <td className="px-4 py-2.5 text-right text-ink-soft tabular-nums">{r.count}</td>
                    <td className="px-4 py-2.5 text-right text-ink-soft tabular-nums">{pct}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TrafficSources({ sources, topReferrers }) {
  const total = (sources.direct || 0) + (sources.google || 0) + (sources.social || 0) + (sources.other || 0);
  const buckets = [
    { key: "direct", label: "Direct", color: "#0E3B4A", desc: "Typed in URL, bookmarks, app links" },
    { key: "google", label: "Google", color: "#3C5A68", desc: "Search results" },
    { key: "social", label: "Social", color: "#7A8F6B", desc: "Facebook, Instagram, Twitter etc" },
    { key: "other", label: "Other sites", color: "#C8BFA4", desc: "Backlinks, blog mentions" },
  ];
  return (
    <div className="bg-cream-soft border border-sand/70 rounded-3xl p-6 md:p-8" data-testid="traffic-sources">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-ink-soft mb-1">Where visitors come from · last 30 days</div>
          <div className="font-serif text-2xl text-ink">
            {total === 0 ? "No source data yet." : `${total.toLocaleString()} page views`}
          </div>
        </div>
      </div>

      {total === 0 ? (
        <div className="text-sm text-ink-soft italic">
          Source data builds up as visitors arrive. Check back after a few days.
        </div>
      ) : (
        <>
          {/* Stacked bar */}
          <div className="flex h-3 rounded-full overflow-hidden bg-sand/40 mb-3">
            {buckets.map((b) => {
              const pct = ((sources[b.key] || 0) / total) * 100;
              if (pct <= 0) return null;
              return (
                <div key={b.key} style={{ width: `${pct}%`, backgroundColor: b.color }} title={`${b.label} · ${sources[b.key]}`} />
              );
            })}
          </div>
          {/* Legend */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-7">
            {buckets.map((b) => {
              const count = sources[b.key] || 0;
              const pct = total ? ((count / total) * 100).toFixed(0) : 0;
              return (
                <div key={b.key} className="bg-cream rounded-xl border border-sand/60 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: b.color }} />
                    <span className="text-[11px] uppercase tracking-[0.18em] text-ink-soft">{b.label}</span>
                  </div>
                  <div className="font-serif text-2xl text-ink">{count}</div>
                  <div className="text-[10px] text-ink-soft">{pct}% · {b.desc}</div>
                </div>
              );
            })}
          </div>

          {/* Top referrers table */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-3">Top referring sites</div>
            <div className="bg-cream rounded-xl border border-sand/60 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-sand/20 text-ink-soft text-[11px] uppercase tracking-[0.16em]">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Source</th>
                    <th className="text-right px-4 py-2 font-medium">Visits</th>
                  </tr>
                </thead>
                <tbody>
                  {topReferrers.slice(0, 10).map((r) => (
                    <tr key={r.host} className="border-t border-sand/40">
                      <td className="px-4 py-2.5 text-ink">{r.label}</td>
                      <td className="px-4 py-2.5 text-right text-ink-soft tabular-nums">{r.count}</td>
                    </tr>
                  ))}
                  {topReferrers.length === 0 && (
                    <tr><td className="px-4 py-4 text-ink-soft italic" colSpan={2}>No referring sites yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Tile({ label, value, icon: Icon, accent }) {
  return (
    <div
      className={`rounded-2xl p-5 border ${
        accent ? "bg-navy text-cream border-navy" : "bg-cream-soft border-sand/70 text-ink"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-[0.22em] opacity-80">{label}</div>
        <Icon size={14} className="opacity-60" />
      </div>
      <div className="font-serif text-4xl leading-none">{value}</div>
    </div>
  );
}

function MiniChart({ title, subtitle, total, series, field, color }) {
  const max = Math.max(1, ...series.map((d) => d[field] || 0));
  return (
    <div className="bg-cream-soft border border-sand/70 rounded-3xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-ink-soft">{title}</div>
          <div className="font-serif text-2xl text-ink">{total}</div>
          {subtitle && <div className="text-[11px] text-ink-soft mt-0.5">{subtitle}</div>}
        </div>
      </div>
      <div className="flex items-end gap-0.5 h-20">
        {series.map((d) => {
          const v = d[field] || 0;
          const h = Math.max(2, Math.round((v / max) * 100));
          return (
            <div
              key={d.date}
              className="flex-1 rounded-t-sm transition-colors"
              style={{ height: `${h}%`, backgroundColor: color, opacity: v ? 0.8 : 0.12 }}
              title={`${d.date}: ${v}`}
            />
          );
        })}
      </div>
    </div>
  );
}


function formatTime(hhmm) {
  if (!hhmm) return "—";
  const [h] = hhmm.split(":").map(Number);
  const suffix = h >= 12 ? "pm" : "am";
  const hour12 = ((h + 11) % 12) + 1;
  return `${hour12}:00 ${suffix}`;
}

function UpcomingSchedule({ data }) {
  const labelFor = (iso) => {
    const today = new Date().toISOString().slice(0, 10);
    const d = new Date(iso + "T00:00:00");
    const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
    if (iso === today) return "Today";
    if (iso === tomorrow) return "Tomorrow";
    return format(d, "EEE d MMM");
  };

  const totalBookings = data.days.reduce((n, d) => n + d.bookings.length, 0);

  return (
    <div className="bg-cream-soft border border-sand/70 rounded-3xl p-6 md:p-8" data-testid="upcoming-schedule">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-ink-soft mb-1">Upcoming schedule</div>
          <div className="font-serif text-2xl text-ink">
            {totalBookings === 0
              ? "Nothing on the calendar yet."
              : `${totalBookings} session${totalBookings > 1 ? "s" : ""} coming up`}
          </div>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {data.days.map((day) => (
          <div key={day.date} className="bg-cream rounded-2xl border border-sand/70 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-navy">{labelFor(day.date)}</div>
                <div className="font-serif text-lg text-ink">
                  {format(new Date(day.date + "T00:00:00"), "d MMMM yyyy")}
                </div>
              </div>
              <div className="text-xs text-ink-soft">
                {day.bookings.length} {day.bookings.length === 1 ? "session" : "sessions"}
              </div>
            </div>
            {day.bookings.length === 0 ? (
              <div className="text-sm italic text-ink-soft font-serif">Quiet day — no sessions.</div>
            ) : (
              <div className="space-y-2">
                {day.bookings.map((b) => (
                  <div
                    key={b.id}
                    className="flex items-center gap-3 bg-cream-soft rounded-xl p-3 border border-sand/50"
                  >
                    <div className="flex-shrink-0 text-center min-w-[68px]">
                      <div className="font-serif text-navy text-lg leading-none">{formatTime(b.start_time)}</div>
                      <div className="text-[10px] uppercase tracking-[0.16em] text-ink-soft mt-0.5">{formatTime(b.end_time)}</div>
                    </div>
                    <div className="h-8 w-px bg-sand/80" />
                    <div className="flex-grow min-w-0">
                      <div className="font-medium text-ink truncate">{b.client_name}</div>
                      <div className="text-xs text-ink-soft truncate flex items-center gap-2">
                        <span className="truncate">{b.service_name}</span>
                        <span>·</span>
                        <MapPin size={10} />
                        <span className="truncate">{b.location_name}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
